import React, { useContext, useEffect, useId, useState } from "react";
import {
  AttributeWindowLinkModel,
  AttributeWindowModel,
  drawingApi,
  DrawingTypeModel,
  LocaleModel,
} from "../api/drawingApi";
import { Button, Container, Form, Modal, Tab, Tabs } from "react-bootstrap";
import AttributeWindowPageEdit from "./AttributeWindowPageEdit";
import { Problem } from "../api/backendApi";
import ErrorDisplay from "../common/ErrorDisplay";
import { Translate } from "react-bootstrap-icons";
import TranslationModal from "./TranslationModal";
import {
  AttributeWindowTextModel,
  glossaryApi,
  LocalizedAttributeWindowTextModel,
} from "../api/glossaryApi";
import TextWarningModal from "./TextWarningModal";
import { DrawingContext } from "./DrawingContext";

interface AttributeWindowEditModalProps {
  name: string | undefined;
  isInformationWindow: boolean;
  nameOptions: string[] | undefined;
  readOnly: boolean;
  onClose: () => void;
  onSave: (name: string, done: boolean | undefined) => void;
}

function AttributeWindowEditModal({
  name: initialName,
  isInformationWindow,
  nameOptions,
  readOnly: initialReadOnly,
  onClose,
  onSave,
}: AttributeWindowEditModalProps) {
  const drawing = useContext(DrawingContext);
  const [error, setError] = useState<Problem | string | undefined>();
  const [readOnly, setReadOnly] = useState(true);
  const [name, setName] = useState(initialName ?? "");
  const [attributeWindows, setAttributeWindows] = useState<
    AttributeWindowModel[]
  >([]);
  // used to work around the issue with using pageNumber as key (forces react to redraw all pages when one of them is deleted)
  const [modifier, setModifier] = useState(0);

  const [tabKey, setTabKey] = useState("0_1");

  const [showTextWarningModal, setShowTextWarningModal] = useState(false);
  const [unmatchedStrings, setUnmatchedStrings] = useState<string[]>([]);
  const [knownUnmatchedStrings, setKnownUmatchedStrings] = useState<string[]>(
    [],
  );

  const [showTextTranslationModal, setShowTextTranslationModal] =
    useState(false);

  const [otherWindows, setOtherWindows] = useState<string[]>([]);

  const doneCheckId = useId();
  const [done, setDone] = useState<boolean>();

  useEffect(() => {
    async function load() {
      if (initialName) {
        const response = await drawingApi.getAttributeWindow(
          drawing.name,
          drawing.type,
          initialName,
          isInformationWindow,
        );

        if (!response.ok) {
          setError(response.problem);
          return;
        }
        // the text is templated, convert to text
        const templates = response.value.map(
          (aw) => new AttributeWindowTextModel(aw.title ?? "", aw.text ?? ""),
        );

        const templatingResponse =
          await glossaryApi.getTranslatedAttributeWindows(templates, [
            LocaleModel.English,
          ]);

        if (!templatingResponse.ok) {
          setError(templatingResponse.problem);
          return;
        }

        let translatedAttributeWindows: AttributeWindowModel[] = [];

        for (let ii = 0; ii < templatingResponse.value.length; ++ii) {
          const translated = templatingResponse.value[ii];

          translatedAttributeWindows.push({
            ...response.value[ii],
            title: translated.title,
            text: translated.text.replaceAll("<NL>", "\r\n"),
          });
        }

        setAttributeWindows(translatedAttributeWindows);

        setDone(response.value[0].done);
      } else {
        setAttributeWindows([{ pageNumber: 1 }]);
        setDone(undefined);
      }

      const otherWindowsResult = await drawingApi.getAttributeWindowReferences(
        drawing.name,
        drawing.type,
      );

      if (otherWindowsResult.ok) {
        setError(undefined);
        setReadOnly(initialReadOnly);
        setOtherWindows(
          initialName
            ? otherWindowsResult.value.filter((ow) => ow !== initialName)
            : otherWindowsResult.value,
        );
      } else {
        setError(otherWindowsResult.problem);
      }
    }

    load();
  }, [
    drawing.name,
    drawing.type,
    initialName,
    initialReadOnly,
    isInformationWindow,
  ]);

  const handleSave = async (skipWarnings: boolean) => {
    // normalize new lines
    const normalized = attributeWindows.map((aw, index) => ({
      ...aw,
      ...{
        name: name,
        pageNumber: index + 1,
        text: aw.text?.replaceAll(/(?:\r\n|\r|\n)/g, "<NL>") ?? "",
      },
    }));

    // templatize (if it's a vehicle file and not a template)
    if (drawing.type === DrawingTypeModel.Drawing) {
      const attributeWindowTexts = normalized.map(
        (aw) =>
          new LocalizedAttributeWindowTextModel(
            aw.title ?? "",
            aw.text ?? "",
            LocaleModel.English,
          ),
      );

      const result = await glossaryApi.getTemplatedAttributeWindows(
        drawing.name,
        attributeWindowTexts,
      );

      if (!result.ok) {
        setError(result.problem);
        return;
      }

      for (let ii = 0; ii < normalized.length; ++ii) {
        const aw = normalized[ii];

        aw.title = result.value.templates[ii].title;
        aw.text = result.value.templates[ii].text;
      }

      if (
        !skipWarnings &&
        (result.value.unmatchedStrings.length > 0 ||
          result.value.knownUnmatchedStrings.length > 0)
      ) {
        setUnmatchedStrings(result.value.unmatchedStrings);
        setKnownUmatchedStrings(result.value.knownUnmatchedStrings);
        setShowTextWarningModal(true);

        return;
      }
    }

    const result = await drawingApi.upsertAttributeWindow(
      drawing.name,
      drawing.type,
      initialName,
      isInformationWindow,
      normalized.map((aw) => ({ ...aw, name: name, done: done })),
    );

    if (result.ok) {
      setError(undefined);
      onSave(name, done);
      onClose();
    } else {
      setError(result.problem);
    }
  };

  const modifyTitle = (
    attributeWindow: AttributeWindowModel,
    newTitle: string,
  ) => {
    setAttributeWindows((previous) =>
      previous.map((window) => (window.pageNumber === attributeWindow.pageNumber ? { ...window, title: newTitle } : window)),
    );
  };

  const modifyText = (
    attributeWindow: AttributeWindowModel,
    newText: string,
  ) => {
    setAttributeWindows((previous) =>
      previous.map((window) => (window.pageNumber === attributeWindow.pageNumber ? { ...window, text: newText } : window)),
    );
  };

  const modifyAttributeTextErrors = (
    attributeWindow: AttributeWindowModel,
    textErrors: string[],
  ) => {
    setAttributeWindows((previous) =>
      previous.map((window) => (window.pageNumber === attributeWindow.pageNumber ? { ...window, textErrors: textErrors } : window)),
    );
  }

  const modifyLinks = (
    attributeWindow: AttributeWindowModel,
    newLinks: AttributeWindowLinkModel[],
  ) => {
    setAttributeWindows((previous) =>
      previous.map((window) => (window.pageNumber === attributeWindow.pageNumber ? { ...window, links: newLinks } : window)),
    );
  };

  const handleClose = () => {
    setError(undefined);
    onClose();
  };

  const handleAddPage = () => {
    const modifiedAttributeWindows = [
      ...attributeWindows,
      { title: "new page", pageNumber: attributeWindows.length + 1 },
    ];

    setAttributeWindows(modifiedAttributeWindows);
    setTabKey(
      modifier.toString() + "_" + modifiedAttributeWindows.length.toString(),
    );
  };

  const handleRemovePage = () => {
    // ensure some things are valid first
    const currentPage = Number(tabKey.substring(tabKey.indexOf("_") + 1));

    if (!isFinite(currentPage)) return;

    const attributeWindowToRemove = attributeWindows.find(
      (aw) => aw.pageNumber === currentPage,
    );

    if (!attributeWindowToRemove) return;

    if (!window.confirm("Are you sure you want to remove the current page?")) {
      return;
    }

    const modifiedAttributeWindows = attributeWindows
      .filter((aw) => aw !== attributeWindowToRemove)
      .map((aw, index) => ({ ...aw, pageNumber: index + 1 }));

    setAttributeWindows(modifiedAttributeWindows);

    setTabKey(
      (modifier + 1).toString() +
        "_" +
        Math.max(modifiedAttributeWindows.length, currentPage).toString(),
    );
    setModifier(modifier + 1); // force refresh of the pages
  };

  const handleDoneClicked = (e: React.MouseEvent<HTMLInputElement>) => {
    setDone((d) => {
      let newValue: boolean | undefined;
      if (d === undefined) newValue = true;
      else if (d) newValue = false;
      else newValue = undefined;

      return newValue;
    });
  };

  // this is taken directly from bootstrap's CSS and allows showing the indeterminate state without the problems associated with refs and executing javascript
  //    to set the indeterminate value
  const indeterminateCheckBoxStyle: React.CSSProperties = {
    backgroundColor: "#0d6efd",
    borderColor: "#0d6efd",
    backgroundImage:
      "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3e%3cpath fill='none' stroke='%23fff' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='M6 10h8'/%3e%3c/svg%3e\")",
  };

  return (
    <>
      <Modal
        backdrop="static"
        show={true}
        onHide={() => handleClose()}
        size="xl"
        style={
          showTextWarningModal || showTextTranslationModal
            ? { filter: "blur(3px)" }
            : {}
        }
      >
        <Modal.Header closeButton={true}>
          <Modal.Title>
            {initialName ?? "New Attribute Window"}{" "}
            <Translate
              role="button"
              onClick={() => setShowTextTranslationModal(true)}
            />
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Container>
            <ErrorDisplay error={error} />
            <Form.Group>
              <Form.Label>Name</Form.Label>
              {nameOptions && !readOnly ? (
                <Form.Select
                  defaultValue={initialName}
                  onChange={(e) => setName(e.target.value)}
                >
                  {(!initialName || initialName.length === 0) && (
                    <option value="">[select]</option>
                  )}
                  {nameOptions.map((no) => (
                    <option key={no} value={no}>
                      {no}
                    </option>
                  ))}
                </Form.Select>
              ) : (
                <Form.Control
                  defaultValue={initialName}
                  readOnly={readOnly}
                  onChange={(e) => setName(e.target.value)}
                />
              )}
            </Form.Group>
            {attributeWindows.length > 1 ? (
              <Tabs
                className="my-2"
                activeKey={tabKey}
                onSelect={(k) => setTabKey(k ?? "1")}
              >
                {attributeWindows.map((aw) => (
                  <Tab
                    className="p-2"
                    style={{ backgroundColor: "#f8f8f8" }}
                    eventKey={`${modifier.toString()}_${aw.pageNumber}`}
                    title={"Page " + aw.pageNumber}
                    key={`${modifier.toString()}_${aw.pageNumber}`}
                  >
                    <AttributeWindowPageEdit
                      title={aw.title ?? ""}
                      text={aw.text ?? ""}
                      links={aw.links ?? []}
                      otherWindows={otherWindows}
                      onTitleChange={(newTitle) => modifyTitle(aw, newTitle)}
                      onTextChange={(newText) => modifyText(aw, newText)}
                      onErrorChange={(textErrors) => modifyAttributeTextErrors(aw, textErrors)}
                      onLinksChange={(newLinks) => modifyLinks(aw, newLinks)}
                      readOnly={readOnly}
                      onError={(e) => setError(e)}
                    />
                  </Tab>
                ))}
              </Tabs>
            ) : (
              <AttributeWindowPageEdit
                title={attributeWindows[0]?.title ?? ""}
                text={attributeWindows[0]?.text ?? ""}
                links={attributeWindows[0]?.links ?? []}
                otherWindows={otherWindows}
                onTitleChange={(newTitle) =>
                  modifyTitle(attributeWindows[0], newTitle)
                }
                onTextChange={(newText) =>
                  modifyText(attributeWindows[0], newText)
                }
                onErrorChange={(textErrors) => 
                  modifyAttributeTextErrors(attributeWindows[0], textErrors)
                }
                readOnly={readOnly}
                onLinksChange={(newLinks) =>
                  modifyLinks(attributeWindows[0], newLinks)
                }
                onError={(e) => setError(e)}
              />
            )}
          </Container>
        </Modal.Body>
        <Modal.Footer>
          {drawing.type === DrawingTypeModel.Drawing &&
            attributeWindows.length > 0 && (
              <div className="form-check" style={{ marginRight: "auto" }}>
                {done === undefined ? (
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={false}
                    onClick={handleDoneClicked}
                    id={doneCheckId}
                    readOnly={attributeWindows.length === 0 || readOnly}
                    style={indeterminateCheckBoxStyle}
                  />
                ) : (
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={done}
                    onClick={handleDoneClicked}
                    id={doneCheckId}
                    readOnly={attributeWindows.length === 0 || readOnly}
                  />
                )}
                <label
                  className="form-check-label"
                  htmlFor={doneCheckId}
                  style={{ userSelect: "none" }}
                >
                  Done
                </label>
              </div>
            )}

          {!readOnly && (
            <Button onClick={() => handleAddPage()}>Add page</Button>
          )}
          {!readOnly && (
            <Button
              disabled={attributeWindows.length < 2}
              onClick={() => handleRemovePage()}
            >
              Remove page
            </Button>
          )}
          <Button onClick={() => handleClose()}>Cancel</Button>
          <Button disabled={
            readOnly ||
            (attributeWindows.some(attributeWindow => attributeWindow.textErrors && attributeWindow.textErrors.length > 0))}
            onClick={() => handleSave(false)}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>
      {showTextWarningModal && (
        <TextWarningModal
          unmatchedStrings={unmatchedStrings}
          knownUnmatchedStrings={knownUnmatchedStrings}
          onSave={() => {
            setShowTextWarningModal(false);
            handleSave(true);
          }}
          onClose={() => setShowTextWarningModal(false)}
        />
      )}

      {showTextTranslationModal && (
        <TranslationModal
          attributeWindows={attributeWindows}
          onClose={() => setShowTextTranslationModal(false)}
        />
      )}
    </>
  );
}

export default AttributeWindowEditModal;
